import React, { useEffect, useId, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle, ArrowLeft, BadgeCheck, BarChart3, Bell, Building2, CalendarDays,
  ChevronDown, ChevronUp, Download, ExternalLink, FileSearch, FileText, Gavel, Layers, Loader2, MessageSquare, Package,
  ScrollText, ShieldAlert, Tag, TrendingUp, Users,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import ProcurementAccessGate from '../components/ProcurementAccessGate';
import { useProcurementAccess } from '../context/ProcurementAccessContext';
import Pagination from '../components/Pagination';
import TrackProcurementDialog from '../components/TrackProcurementDialog';
import TenderRevisionNotice from '../components/TenderRevisionNotice';
import LotDetailTabs, { LotDetailTabKey } from '../components/LotDetailTabs';
import {
  LotDetailResponse, LotDocument, LotInsightsResponse, LotRefsResponse, LotSupplierSort, SupplierInsight, fmtAmount, fmtDate,
  getLotDetail, getLotInsights, getLotRefs, getLotSuppliers, lotStatusTone,
} from '../api/lots';

const TONE_CLASSES: Record<string, string> = {
  green: 'bg-emerald-50 text-emerald-700',
  blue: 'bg-sky-50 text-sky-700',
  gray: 'bg-gray-100 text-gray-600',
  red: 'bg-red-50 text-red-600',
};

const SUPPLIERS_PAGE = 20;
const SUPPLIERS_PREVIEW = 3;

function downloadUrl(value?: string | null): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    return ['https:', 'http:'].includes(url.protocol) && !url.username && !url.password ? url.href : null;
  } catch { return null; }
}

function documentUrl(item: LotDocument, language: string): string | null {
  return downloadUrl(language === 'kk' ? item.url_kz : item.url_ru)
    || downloadUrl(item.url)
    || downloadUrl(language === 'kk' ? item.url_ru : item.url_kz);
}

function DocumentList({ items }: { items: LotDocument[] }) {
  const { t, language } = useLanguage();
  const unique = items.filter((item, index) => documentUrl(item, language)
    && items.findIndex((other) => documentUrl(other, language) === documentUrl(item, language)) === index);
  return (
    <ul className="space-y-2">
      {unique.map((item) => {
        const name = (language === 'kk' ? item.name_kz || item.name_ru : item.name_ru || item.name_kz)
          || t(`lots.detail.documentKinds.${item.kind || 'document'}`);
        return (
          <li key={documentUrl(item, language)!}>
            <a href={documentUrl(item, language)!} target="_blank" rel="noopener noreferrer"
               className="flex items-center gap-3 rounded-xl border border-gray-100 p-3 text-sm font-semibold text-goszakup-700 hover:bg-goszakup-50">
              <Download className="w-4 h-4 shrink-0" />
              <span>{name}{item.number && <span className="ml-2">№ {item.number}</span>}</span>
              <span className="ml-auto text-xs">{t('lots.detail.downloadDocument')}</span>
            </a>
          </li>
        );
      })}
    </ul>
  );
}

type TabKey = LotDetailTabKey;

function Row({ label, value, strong = false }: { label: string; value: React.ReactNode; strong?: boolean }) {
  if (value == null || value === '' || value === '—') return null;
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-4 py-2 border-b border-gray-50 last:border-0">
      <dt className="sm:w-64 shrink-0 text-xs font-bold text-gray-400">{label}</dt>
      <dd className={`text-sm ${strong ? 'font-extrabold text-gray-900' : 'text-gray-800'}`}>{value}</dd>
    </div>
  );
}

function EmptyTab({ text, url, urlLabel }: { text: string; url?: string | null; urlLabel?: string }) {
  return (
    <div className="text-center py-14 text-gray-400">
      <FileText className="w-10 h-10 mx-auto opacity-30 mb-3" />
      <p className="text-sm max-w-md mx-auto">{text}</p>
      {url && (
        <a href={url} target="_blank" rel="noreferrer"
           className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-goszakup-600 hover:text-goszakup-700">
          {urlLabel} <ExternalLink className="w-3.5 h-3.5" />
        </a>
      )}
    </div>
  );
}

export default function LotDetailPage() {
  return <ProcurementAccessGate feature="lots"><LotDetailContent /></ProcurementAccessGate>;
}
function LotDetailContent() {
  const { allowed } = useProcurementAccess();
  const canAnalyze = allowed('lot_analytics');
  const canViewSuppliers = allowed('possible_suppliers');
  const { id } = useParams<{ id: string }>();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [data, setData] = useState<LotDetailResponse | null>(null);
  const [insights, setInsights] = useState<LotInsightsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [insightsLoading, setInsightsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState<TabKey>('general');
  const tabsId = useId();
  const [refs, setRefs] = useState<LotRefsResponse | null>(null);
  const [trackingOpen, setTrackingOpen] = useState(false);
  const [trackedId, setTrackedId] = useState<number | null>(null);

  useEffect(() => { getLotRefs().then(setRefs).catch(() => {}); }, []);

  // полный список возможных поставщиков (пагинация)
  const [suppliers, setSuppliers] = useState<SupplierInsight[]>([]);
  const [suppliersTotal, setSuppliersTotal] = useState(0);
  const [suppliersPage, setSuppliersPage] = useState(0);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [suppliersExpanded, setSuppliersExpanded] = useState(false);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [supplierQuery, setSupplierQuery] = useState('');
  const [supplierSort, setSupplierSort] = useState<LotSupplierSort>('wins');
  const [supplierOrder, setSupplierOrder] = useState<'asc' | 'desc'>('desc');
  const [suppliersError, setSuppliersError] = useState(false);
  const [suppliersPartial, setSuppliersPartial] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => { setSupplierQuery(supplierSearch.trim()); setSuppliersPage(0); }, 250);
    return () => window.clearTimeout(timer);
  }, [supplierSearch]);

  useEffect(() => {
    const lotId = Number(id);
    if (!lotId) { setNotFound(true); setLoading(false); return; }
    setLoading(true);
    setNotFound(false);
    setTab('general');
    setSuppliersPage(0);
    setSuppliersExpanded(false);
    setSupplierSearch('');
    setSupplierQuery('');
    setSuppliers([]);
    setSuppliersTotal(0);
    setInsights(null);
    let active = true;
    getLotDetail(lotId)
      .then((result) => { if (active) setData(result); })
      .catch(() => { if (active) setNotFound(true); })
      .finally(() => { if (active) setLoading(false); });
    setInsightsLoading(canAnalyze);
    if (canAnalyze) getLotInsights(lotId)
      .then((result) => { if (active) setInsights(result); })
      .catch(() => { if (active) setInsights(null); })
      .finally(() => { if (active) setInsightsLoading(false); });
    return () => { active = false; };
  }, [id, canAnalyze]);

  useEffect(() => {
    const lotId = Number(id);
    if (!lotId || !canViewSuppliers || tab !== 'analytics') return;
    const controller = new AbortController();
    let active = true;
    setSuppliersLoading(true);
    setSuppliersError(false);
    setSuppliersPartial(false);
    getLotSuppliers(lotId, {
      skip: suppliersExpanded ? suppliersPage * SUPPLIERS_PAGE : 0,
      limit: suppliersExpanded ? SUPPLIERS_PAGE : SUPPLIERS_PREVIEW,
      q: supplierQuery || undefined, sort: supplierSort, order: supplierOrder, language,
    }, controller.signal)
      .then((r) => { if (active) { setSuppliers(r.items); setSuppliersTotal(r.total); setSuppliersPartial(!!r.partial); } })
      .catch(() => { if (active) { setSuppliers([]); setSuppliersTotal(0); setSuppliersError(true); } })
      .finally(() => { if (active) setSuppliersLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [id, canViewSuppliers, tab, suppliersPage, suppliersExpanded, supplierQuery, supplierSort, supplierOrder, language]);

  const toggleSupplierList = () => { setSuppliersExpanded((value) => !value); setSuppliersPage(0); };
  const sortSuppliers = (column: LotSupplierSort) => {
    setSupplierOrder(column === supplierSort ? (supplierOrder === 'asc' ? 'desc' : 'asc') : column === 'supplier' ? 'asc' : 'desc');
    setSupplierSort(column);
    setSuppliersPage(0);
  };

  const pick = (ru?: string | null, kk?: string | null) => (language === 'kk' ? kk || ru : ru || kk) || '—';
  const refName = (r: { name_ru: string | null; name_kz: string | null } | null) =>
    r ? (language === 'kk' ? r.name_kz || r.name_ru : r.name_ru) : null;
  const lotStatusName = (statusId: number | null) => {
    const r = refs?.statuses.find((x) => x.id === statusId);
    return r ? (language === 'kk' ? r.name_kz || r.name_ru : r.name_ru) : null;
  };

  const tabs = useMemo(() => ([
    { key: 'general' as TabKey, label: t('lots.tabs.general'), icon: <Package className="w-4 h-4" /> },
    { key: 'analytics' as TabKey, label: t('lots.tabs.analytics'), icon: <BarChart3 className="w-4 h-4" /> },
    { key: 'lots' as TabKey, label: t('lots.tabs.lots'), icon: <Layers className="w-4 h-4" /> },
    { key: 'documents' as TabKey, label: t('lots.tabs.documents'), icon: <FileText className="w-4 h-4" /> },
    { key: 'discussion' as TabKey, label: t('lots.tabs.discussion'), icon: <MessageSquare className="w-4 h-4" /> },
    { key: 'protocols' as TabKey, label: t('lots.tabs.protocols'), icon: <ScrollText className="w-4 h-4" /> },
    { key: 'contracts' as TabKey, label: t('lots.tabs.contracts'), icon: <Gavel className="w-4 h-4" /> },
    { key: 'appeal' as TabKey, label: t('lots.tabs.appeal'), icon: <ShieldAlert className="w-4 h-4" /> },
  ]), [t]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center text-gray-400">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center text-gray-400 px-4">
        <FileSearch className="w-14 h-14 opacity-30 mb-4" />
        <p className="font-bold text-gray-600 mb-1">{t('lots.detail.notFound')}</p>
        <Link to="/lots" className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-goszakup-600 text-white text-sm font-semibold hover:bg-goszakup-700">
          <ArrowLeft className="w-4 h-4" /> {t('lots.detail.backToSearch')}
        </Link>
      </div>
    );
  }

  const { lot, trd_buy: buy } = data;
  const enstruCodes = Array.isArray(lot.enstruCode)
    ? lot.enstruCode : lot.enstruCode ? [lot.enstruCode] : [];
  const statusName = refName(data.lot_status);
  const methodName = refName(data.trade_method);
  const buyStatus = data.announcement_status || data.buy_status;
  const buyStatusName = refName(buyStatus);
  const subjectTypeName = refName(data.subject_type);
  const tone = TONE_CLASSES[lotStatusTone(lot.refLotStatusId)];
  const suppliersPages = Math.ceil(suppliersTotal / SUPPLIERS_PAGE);
  const protocols = (data.protocols || []).filter((item) => documentUrl(item, language));
  const documents = [...(data.documents || []), ...protocols].filter((item) => documentUrl(item, language));

  const keyFacts = [
    { label: t('lots.detail.amount'), value: `${fmtAmount(lot.amount)} ₸`, hl: true },
    { label: t('lots.detail.annoStatus'), value: buyStatusName || '—', toneClass: buyStatus?.id === 190 ? 'bg-amber-50 text-amber-800' : 'bg-slate-100 text-slate-700' },
    { label: t('lots.filters.status'), value: statusName || '—', toneClass: tone },
    { label: t('lots.detail.publishDate'), value: buy ? fmtDate(buy.publishDate) : '—' },
    { label: t('lots.detail.endDate'), value: buy ? fmtDate(buy.endDate) : '—' },
    { label: t('lots.detail.itogiDate'), value: buy ? fmtDate(buy.itogiDatePublic) : '—' },
  ];

  const supplierName = (s: { name_ru?: string | null; name_kz?: string | null; bin: string }) => {
    const n = language === 'kk' ? s.name_kz || s.name_ru : s.name_ru || s.name_kz;
    return n || s.bin;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Шапка */}
      <div className="bg-gradient-to-b from-white to-slate-50 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <Link to="/lots" className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-400 hover:text-goszakup-600 mb-4">
            <ArrowLeft className="w-4 h-4" /> {t('lots.detail.backToSearch')}
          </Link>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="text-xs font-bold text-gray-400">№ {lot.lotNumber || lot.id}</span>
            {buy?.numberAnno && <span className="text-xs font-bold text-gray-400">{t('lots.card.anno')} {buy.numberAnno}</span>}
            {methodName && (
              <span className="text-xs font-bold text-goszakup-700 bg-goszakup-50 px-2.5 py-1 rounded-full inline-flex items-center gap-1">
                <Tag className="w-3 h-3" />{methodName}
              </span>
            )}
            {data.goszakup_url && (
              <a href={data.goszakup_url} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1 text-xs text-goszakup-600 hover:text-goszakup-700 font-semibold">
                {t('lots.detail.onGoszakup')} <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
          <h1 className="text-xl sm:text-2xl font-extrabold text-gray-900 tracking-tight mb-1.5">
            {pick(lot.nameRu, lot.nameKz)}
          </h1>
          <p className="flex flex-wrap items-center gap-1.5 text-sm text-gray-500 mb-4">
            <Building2 aria-hidden="true" className="w-4 h-4 shrink-0" /><span className="min-w-0 [overflow-wrap:anywhere]">{pick(lot.customerNameRu, lot.customerNameKz)}</span>
            {lot.customerBin && <span className="text-gray-400 break-all">· {lot.customerBin}</span>}
          </p>
          <TenderRevisionNotice revision={data.announcement_revision} />
          <div className="mb-5 flex flex-wrap items-center gap-3">
            <button type="button" onClick={() => setTrackingOpen(true)} className="procurement-button procurement-button-primary">
              <Bell aria-hidden="true" className="h-4 w-4 shrink-0" />{t(trackedId === Number(lot.id) ? 'tracking.tracked' : 'tracking.add')}
            </button>
            <Link to="/tracker" className="procurement-button">{t('nav.tracker')}</Link>
            <p className="w-full text-xs leading-relaxed text-slate-500">{t('tracking.description')}</p>
          </div>

          {/* Ключевые показатели — сразу бросаются в глаза */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {keyFacts.map((f, i) => (
              <div key={i} className={`rounded-2xl p-4 ${f.hl ? 'bg-goszakup-600 text-white' : 'bg-white border border-gray-100 shadow-sm'}`}>
                <p className={`text-[11px] font-bold mb-1 ${f.hl ? 'text-white/70' : 'text-gray-400'}`}>{f.label}</p>
                {f.toneClass ? (
                  <span className={`inline-block text-xs font-extrabold px-2.5 py-1 rounded-lg ${f.toneClass}`}>{f.value}</span>
                ) : (
                  <p className={`font-extrabold ${f.hl ? 'text-white text-lg' : 'text-gray-900'}`}>{f.value}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <LotDetailTabs id={tabsId} label={t('lots.detail.sectionsLabel')} items={tabs} active={tab} onChange={setTab} />

        <div role="tabpanel" id={`${tabsId}-panel`} aria-labelledby={`${tabsId}-${tab}-tab`} tabIndex={0}
          className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-goszakup-500 focus-visible:ring-offset-2">
          {/* ── Общие сведения ── */}
          {tab === 'general' && (
            <div className="space-y-8">
              <div>
                <h2 className="font-extrabold text-gray-900 mb-3">{t('lots.detail.annoInfo')}</h2>
                {buy ? (
                  <dl>
                    <Row label={t('lots.detail.annoNumber')} value={buy.numberAnno} strong />
                    <Row label={t('lots.detail.annoName')} value={pick(buy.nameRu, buy.nameKz)} />
                    <Row label={t('lots.detail.annoStatus')} value={buyStatusName && (
                      <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-sky-50 text-sky-700">{buyStatusName}</span>
                    )} />
                    <Row label={t('lots.detail.tradeMethod')} value={methodName} />
                    <Row label={t('lots.detail.subjectType')} value={subjectTypeName} />
                    <Row label={t('lots.detail.organizer')} value={pick(buy.orgNameRu, buy.orgNameKz)} />
                    <Row label={t('lots.detail.orgBin')} value={buy.orgBin} />
                    <Row label={t('lots.detail.totalSum')} value={buy.totalSum != null ? `${fmtAmount(buy.totalSum)} ₸` : null} strong />
                    <Row label={t('lots.detail.lotsCount')} value={buy.countLots} />
                    <Row label={t('lots.detail.finYear')} value={buy.finYear} />
                    <Row label={t('lots.detail.publishDate')} value={fmtDate(buy.publishDate)} strong />
                    <Row label={t('lots.detail.startDate')} value={fmtDate(buy.startDate)} />
                    <Row label={t('lots.detail.endDate')} value={fmtDate(buy.endDate)} strong />
                    <Row label={t('lots.detail.itogiDate')} value={fmtDate(buy.itogiDatePublic)} />
                  </dl>
                ) : (
                  <p className="text-sm text-gray-400">{t('lots.detail.noAnno')}</p>
                )}
              </div>

              <div>
                <h2 className="font-extrabold text-gray-900 mb-3">{t('lots.detail.lotInfo')}</h2>
                <dl>
                  <Row label={t('lots.detail.lotNumber')} value={lot.lotNumber} strong />
                  <Row label={t('lots.detail.description')} value={pick(lot.descriptionRu, lot.descriptionKz)} />
                  <Row label={t('lots.detail.count')} value={lot.count != null ? fmtAmount(lot.count) : null} />
                  <Row label={t('lots.detail.amount')} value={`${fmtAmount(lot.amount)} ₸`} strong />
                  <Row label={t('lots.detail.customerBin')} value={lot.customerBin} />
                  <Row
                    label={t('lots.detail.enstru')}
                    value={enstruCodes.length > 1 ? (
                      <span className="flex flex-wrap gap-2">
                        {enstruCodes.map((code) => (
                          <Link key={code} to={`/lots?enstru=${encodeURIComponent(code)}`}
                                title={data.enstru?.code === code ? pick(data.enstru.nameRu, data.enstru.nameKz) : undefined}
                                className="rounded-lg bg-goszakup-50 px-2.5 py-1 text-xs font-semibold text-goszakup-700 hover:bg-goszakup-100">
                            {code}
                          </Link>
                        ))}
                      </span>
                    ) : data.enstru ? (
                      <span>
                        <Link to={`/lots?enstru=${encodeURIComponent(data.enstru.code)}&cat_label=${encodeURIComponent(pick(data.enstru.nameRu, data.enstru.nameKz))}`} className="text-goszakup-600 hover:underline font-semibold">
                          {pick(data.enstru.nameRu, data.enstru.nameKz)}
                        </Link>
                        <span className="text-gray-400 text-xs ml-2">{data.enstru.code}</span>
                      </span>
                    ) : enstruCodes.join(', ')}
                  />
                  <Row label={t('lots.detail.updated')} value={fmtDate(lot.indexDate)} />
                </dl>
              </div>

              {data.plan_points.length > 0 && (
                <div>
                  <h2 className="font-extrabold text-gray-900 mb-3">{t('lots.detail.planInfo')}</h2>
                  <div className="space-y-3">
                    {data.plan_points.map((p) => (
                      <div key={p.id} className="rounded-xl border border-gray-100 p-4">
                        <p className="text-sm font-semibold text-gray-800 mb-1">{pick(p.nameRu, p.nameKz)}</p>
                        {p.extraDescRu && <p className="text-xs text-gray-500 mb-2 line-clamp-3">{p.extraDescRu}</p>}
                        <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-500">
                          {p.price != null && <span>{t('lots.detail.planPrice')}: <b className="text-gray-800">{fmtAmount(p.price)} ₸</b></span>}
                          {p.count != null && <span>{t('lots.detail.count')}: <b className="text-gray-800">{fmtAmount(p.count)}</b></span>}
                          {p.supplyDateRu && <span className="inline-flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" />{p.supplyDateRu}</span>}
                          {p.plnPointYear && <span>{t('lots.detail.planYear')}: {p.plnPointYear}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Лоты объявления ── */}
          {tab === 'lots' && (
            data.anno_lots.length === 0 ? (
              <EmptyTab text={t('lots.detail.noAnnoLots')} />
            ) : (
              <div className="overflow-x-auto -mx-2">
                <table className="w-full text-sm min-w-[720px]">
                  <thead>
                    <tr className="text-left text-[11px] font-bold text-gray-400 uppercase border-b border-gray-100">
                      <th className="px-2 py-2">{t('lots.table.lotNumber')}</th>
                      <th className="px-2 py-2 w-[40%]">{t('lots.table.name')}</th>
                      <th className="px-2 py-2 text-right">{t('lots.table.count')}</th>
                      <th className="px-2 py-2 text-right">{t('lots.table.amount')}</th>
                      <th className="px-2 py-2">{t('lots.table.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.anno_lots.map((al: any) => {
                      const current = al.id === lot.id;
                      return (
                        <tr
                          key={al.id}
                          onClick={() => { if (!current) navigate(`/lots/${al.id}`); }}
                          className={`border-b border-gray-50 last:border-0 transition-colors ${
                            current ? 'bg-goszakup-50/60' : 'cursor-pointer hover:bg-gray-50'
                          }`}
                        >
                          <td className="px-2 py-2.5 font-bold text-goszakup-700 whitespace-nowrap">
                            {al.lotNumber || al.id}
                            {current && <span className="block text-[10px] font-bold text-goszakup-500">{t('lots.detail.currentLot')}</span>}
                          </td>
                          <td className="px-2 py-2.5"><span className="line-clamp-2 text-gray-800">{pick(al.nameRu, al.nameKz)}</span></td>
                          <td className="px-2 py-2.5 text-right text-gray-600 whitespace-nowrap">{al.count != null ? fmtAmount(al.count) : '—'}</td>
                          <td className="px-2 py-2.5 text-right font-bold text-gray-900 whitespace-nowrap">{fmtAmount(al.amount)} ₸</td>
                          <td className="px-2 py-2.5">
                            <span className={`inline-block text-[11px] font-bold px-2 py-0.5 rounded-full ${TONE_CLASSES[lotStatusTone(al.refLotStatusId)]}`}>
                              {lotStatusName(al.refLotStatusId) || al.refLotStatusId || '—'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          )}

          {/* ── Документация ── */}
          {tab === 'documents' && (
            documents.length ? (
              <>
                <DocumentList items={documents} />
                {data.documents_partial && <p className="text-xs text-gray-500 mt-4">{t('lots.detail.documentsPartial')}</p>}
                {data.goszakup_url && data.documents_partial && (
                  <a href={data.goszakup_url} target="_blank" rel="noopener noreferrer" className="inline-block mt-2 text-sm text-goszakup-700 hover:underline">
                    {t('lots.detail.onGoszakup')}
                  </a>
                )}
              </>
            ) : <EmptyTab text={t('lots.detail.docsUnavailable')} url={data.goszakup_url} urlLabel={t('lots.detail.onGoszakup')} />
          )}

          {/* ── Обсуждение положений документации ── */}
          {tab === 'discussion' && (
            buy && (buy.discusStartDate || buy.discusEndDate) ? (
              <dl>
                <Row label={t('lots.detail.discusStart')} value={fmtDate(buy.discusStartDate)} strong />
                <Row label={t('lots.detail.discusEnd')} value={fmtDate(buy.discusEndDate)} strong />
                <p className="text-xs text-gray-400 mt-4">{t('lots.detail.discusHint')}</p>
              </dl>
            ) : (
              <EmptyTab text={t('lots.detail.noDiscussion')} url={data.goszakup_url} urlLabel={t('lots.detail.onGoszakup')} />
            )
          )}

          {/* ── Протоколы (заявки участников) ── */}
          {tab === 'protocols' && (
            <>
              <div className="mb-6">
                {protocols.length ? <DocumentList items={protocols} /> : (
                  <EmptyTab text={t('lots.detail.noProtocolFiles')} url={data.goszakup_url} urlLabel={t('lots.detail.onGoszakup')} />
                )}
                {data.documents_partial && protocols.length > 0 && <p className="text-xs text-gray-500 mt-4">{t('lots.detail.documentsPartial')}</p>}
              </div>
            {data.apps.length === 0 ? (
              <EmptyTab text={t('lots.detail.noApps')} url={data.goszakup_url} urlLabel={t('lots.detail.onGoszakup')} />
            ) : (
              <>
                <p className="text-xs text-gray-400 mb-3">{t('lots.detail.appsHint')}</p>
                <div className="overflow-x-auto -mx-2">
                  <table className="w-full text-sm min-w-[720px]">
                    <thead>
                      <tr className="text-left text-[11px] font-bold text-gray-400 uppercase border-b border-gray-100">
                        <th className="px-2 py-2">{t('lots.detail.supplier')}</th>
                        <th className="px-2 py-2">{t('lots.detail.dateApply')}</th>
                        <th className="px-2 py-2">{t('lots.detail.protNumber')}</th>
                        <th className="px-2 py-2 text-right">{t('lots.detail.appLotPrice')}</th>
                        <th className="px-2 py-2 text-center">{t('lots.detail.appLotsCount')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.apps.map((a) => (
                        <tr key={a.id} className="border-b border-gray-50 last:border-0">
                          <td className="px-2 py-2.5">
                            {a.supplierBinIin ? (
                              <Link to={`/suppliers/${a.supplierBinIin}`} className="font-semibold text-goszakup-700 hover:underline">
                                {supplierName({ name_ru: a.supplierNameRu, name_kz: a.supplierNameKz, bin: a.supplierBinIin })}
                              </Link>
                            ) : (language === 'kk' ? a.supplierNameKz || a.supplierNameRu : a.supplierNameRu || a.supplierNameKz) || '—'}
                            {a.supplierBinIin && <span className="block text-[11px] text-gray-400">{a.supplierBinIin}</span>}
                            {a.lot_membership_known === false && (
                              <span className="block text-xs text-amber-700 mt-1">{t('lots.detail.appLotUnknown')}</span>
                            )}
                          </td>
                          <td className="px-2 py-2.5 text-gray-600 whitespace-nowrap">{fmtDate(a.dateApply)}</td>
                          <td className="px-2 py-2.5 text-gray-600">
                            {a.protNumber && protocols.some((protocol) => protocol.number === a.protNumber) ? (
                              <a href={documentUrl(protocols.find((protocol) => protocol.number === a.protNumber)!, language)!}
                                 target="_blank" rel="noopener noreferrer" className="text-goszakup-700 hover:underline">
                                {a.protNumber}
                              </a>
                            ) : a.protNumber || '—'}
                          </td>
                          <td className="px-2 py-2.5 text-right font-bold text-gray-900 whitespace-nowrap">
                            {a.lotPrice != null ? `${fmtAmount(a.lotPrice)} ₸` : '—'}
                          </td>
                          <td className="px-2 py-2.5 text-center text-gray-600">{a.lot_membership_known === false && !a.lotsCount ? '—' : a.lotsCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            </>
          )}

          {/* ── Договоры ── */}
          {tab === 'contracts' && (
            data.contracts.length === 0 ? (
              <EmptyTab text={t('lots.detail.noContracts')} url={data.goszakup_url} urlLabel={t('lots.detail.onGoszakup')} />
            ) : (
              <div className="overflow-x-auto -mx-2">
                <table className="w-full text-sm min-w-[720px]">
                  <thead>
                    <tr className="text-left text-[11px] font-bold text-gray-400 uppercase border-b border-gray-100">
                      <th className="px-2 py-2">{t('lots.detail.contractNumber')}</th>
                      <th className="px-2 py-2">{t('lots.detail.supplier')}</th>
                      <th className="px-2 py-2">{t('lots.detail.signDate')}</th>
                      <th className="px-2 py-2">{t('lots.table.status')}</th>
                      <th className="px-2 py-2 text-right">{t('lots.detail.contractSum')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.contracts.map((c) => (
                      <tr key={c.id} className="border-b border-gray-50 last:border-0">
                        <td className="px-2 py-2.5 text-gray-600">{c.contractNumber || c.id}</td>
                        <td className="px-2 py-2.5">
                          {c.supplierBiin ? (
                            <Link to={`/suppliers/${c.supplierBiin}`} className="font-semibold text-goszakup-700 hover:underline">
                              {supplierName({ name_ru: c.supplierNameRu, name_kz: c.supplierNameKz, bin: c.supplierBiin })}
                            </Link>
                          ) : '—'}
                          {c.supplierBiin && <span className="block text-[11px] text-gray-400">{c.supplierBiin}</span>}
                        </td>
                        <td className="px-2 py-2.5 text-gray-600 whitespace-nowrap">{fmtDate(c.signDate)}</td>
                        <td className="px-2 py-2.5 text-gray-600">
                          {c.status ? (language === 'kk' ? c.status.name_kz || c.status.name_ru : c.status.name_ru) : '—'}
                        </td>
                        <td className="px-2 py-2.5 text-right font-bold text-gray-900 whitespace-nowrap">{fmtAmount(c.contractSum)} ₸</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}

          {/* ── Апелляции ── */}
          {tab === 'appeal' && (
            <EmptyTab text={t('lots.detail.noAppeals')} url={data.goszakup_url} urlLabel={t('lots.detail.onGoszakup')} />
          )}

          {/* ── Аналитика GLauncher ── */}
          {tab === 'analytics' && (
            <>
            <ProcurementAccessGate feature="lot_analytics" compact>
            {insightsLoading ? (
              <div className="flex justify-center py-10 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : !insights?.available ? (
              <p className="text-sm text-gray-400">{t('lots.detail.analyticsUnavailable')}</p>
            ) : (
              <>
                {insights.price_stats && (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                    {[
                      { label: t('lots.detail.medianAmount'), value: `${fmtAmount(insights.price_stats.median_amount)} ₸`, hl: true },
                      { label: t('lots.detail.avgAmount'), value: `${fmtAmount(insights.price_stats.avg_amount)} ₸` },
                      { label: t('lots.detail.minMax'), value: `${fmtAmount(insights.price_stats.min_amount)} – ${fmtAmount(insights.price_stats.max_amount)} ₸` },
                      { label: t('lots.detail.sample'), value: String(insights.price_stats.sample) },
                    ].map((c, i) => (
                      <div key={i} className={`rounded-xl p-4 ${c.hl ? 'bg-goszakup-600 text-white' : 'bg-slate-50'}`}>
                        <p className={`text-[11px] font-bold mb-1 ${c.hl ? 'text-white/70' : 'text-gray-400'}`}>{c.label}</p>
                        <p className={`font-extrabold ${c.hl ? 'text-white text-lg' : 'text-gray-900'}`}>{c.value}</p>
                      </div>
                    ))}
                  </div>
                )}

                {insights.competition && (
                  <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-600 mb-6">
                    <span className="inline-flex items-center gap-1.5">
                      <TrendingUp className="w-4 h-4 text-goszakup-600" />
                      {t('lots.detail.similarLots')}: <b>{insights.competition.similar_lots}</b>
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Users className="w-4 h-4 text-goszakup-600" />
                      {t('lots.detail.uniqueParticipants')}: <b>{insights.competition.unique_participants}</b>
                    </span>
                    {insights.competition.avg_applications_per_buy != null && (
                      <span className="inline-flex items-center gap-1.5">
                        <BadgeCheck className="w-4 h-4 text-goszakup-600" />
                        {t('lots.detail.avgApps')}: <b>{insights.competition.avg_applications_per_buy}</b>
                      </span>
                    )}
                  </div>
                )}

                {/* Возможные поставщики — полный список с пагинацией */}
              </>
            )}
            </ProcurementAccessGate>
                <ProcurementAccessGate feature="possible_suppliers" compact>
                <h3 className="font-extrabold text-gray-900 mb-1">
                  <button type="button" onClick={toggleSupplierList} aria-expanded={suppliersExpanded}
                          aria-controls="possible-suppliers" className="flex items-center gap-2">
                    {t('lots.detail.possibleSuppliers')}
                    {suppliersTotal > 0 && <span className="text-gray-400 font-bold text-sm">({suppliersTotal.toLocaleString(language === 'kk' ? 'kk-KZ' : 'ru-RU')})</span>}
                    {suppliersExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                </h3>
                <p className="text-xs text-gray-400 mb-3">{t('lots.detail.possibleSuppliersHint')}</p>
                <div id="possible-suppliers">
                <input type="search" value={supplierSearch} maxLength={200}
                       aria-label={t('lots.detail.supplierSearch')} placeholder={t('lots.detail.supplierSearch')}
                       onChange={(event) => { setSupplierSearch(event.target.value); setSuppliersExpanded(true); setSuppliersPage(0); }}
                       className="w-full sm:max-w-md rounded-xl border border-gray-200 px-3 py-2 text-sm mb-3" />
                {suppliersPartial && <p className="text-xs text-amber-700 mb-3">{t('lots.detail.suppliersPartial')}</p>}
                {suppliersLoading ? (
                  <div className="flex justify-center py-10 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
                ) : suppliersError ? (
                  <p role="alert" className="text-sm text-red-600">{t('lots.detail.suppliersError')}</p>
                ) : suppliers.length === 0 ? (
                  <p className="text-sm text-gray-400">{t('lots.detail.noSuppliers')}</p>
                ) : (
                  <>
                    <div className="overflow-x-auto -mx-2">
                      <table className="w-full text-sm min-w-[640px]">
                        <thead>
                          <tr className="text-left text-[11px] font-bold text-gray-400 uppercase border-b border-gray-100">
                            {([
                              ['supplier', 'supplier', 'text-left'],
                              ['participations', 'participations', 'text-center'],
                              ['wins', 'wins', 'text-center'],
                              ['won_sum', 'wonSum', 'text-right'],
                            ] as const).map(([column, label, alignment]) => (
                              <th key={column} className={`px-2 py-2 ${alignment}`}
                                  aria-sort={supplierSort === column ? (supplierOrder === 'asc' ? 'ascending' : 'descending') : 'none'}>
                                <button type="button" onClick={() => sortSuppliers(column)} className="font-bold hover:text-goszakup-700">
                                  {t(`lots.detail.${label}`)} <span aria-hidden="true">{supplierSort === column ? supplierOrder === 'asc' ? '↑' : '↓' : '↕'}</span>
                                </button>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {(suppliersExpanded ? suppliers : suppliers.slice(0, SUPPLIERS_PREVIEW)).map((s) => (
                            <tr key={s.bin} className="border-b border-gray-50 last:border-0">
                              <td className="px-2 py-2.5">
                                <Link to={`/suppliers/${s.bin}`} className="font-semibold text-goszakup-700 hover:underline">
                                  {supplierName(s)}
                                </Link>
                                <span className="flex items-center gap-2 text-[11px] text-gray-400">
                                  {s.bin}
                                  {s.in_rnu && (
                                    <span className="inline-flex items-center gap-0.5 text-red-500 font-bold">
                                      <ShieldAlert className="w-3 h-3" /> {t('lots.detail.inRnu')}
                                    </span>
                                  )}
                                </span>
                              </td>
                              <td className="px-2 py-2.5 text-center text-gray-600">{s.participations ?? '—'}</td>
                              <td className="px-2 py-2.5 text-center font-bold text-gray-900">{s.wins ?? '—'}</td>
                              <td className="px-2 py-2.5 text-right text-gray-600">{s.won_sum != null ? `${fmtAmount(s.won_sum)} ₸` : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {suppliersExpanded && suppliersPages > 1 && (
                      <div className="mt-5">
                        <Pagination
                          currentPage={suppliersPage + 1}
                          totalPages={suppliersPages}
                          onPageChange={(p) => setSuppliersPage(p - 1)}
                        />
                      </div>
                    )}
                  </>
                )}
                {(suppliersTotal > SUPPLIERS_PREVIEW || suppliersExpanded) && (
                  <button type="button" onClick={toggleSupplierList} aria-expanded={suppliersExpanded}
                          className="mt-4 text-sm font-bold text-goszakup-700 hover:underline">
                    {t(suppliersExpanded ? 'lots.detail.showLessSuppliers' : 'lots.detail.showMoreSuppliers')}
                  </button>
                )}
                </div>
                <p className="flex items-start gap-1.5 text-[11px] text-gray-400 mt-4">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  {t('lots.detail.analyticsDisclaimer')}
                </p>
                </ProcurementAccessGate>
              </>
          )}
        </div>
      </div>
      <TrackProcurementDialog open={trackingOpen} onClose={() => setTrackingOpen(false)} lotId={Number(lot.id)}
        numberAnno={buy?.numberAnno || lot.trdBuyNumberAnno || undefined} onTracked={() => setTrackedId(Number(lot.id))} />
    </div>
  );
}
